/**
 * Compact "?" help next to a field label — ora's standard help anchor
 * (`QTooltipAnchorIcon` inside `CustomTooltip`, as `Subheader` and
 * `SortableAgGridHeader` pair them), so the launch form's help reads the same
 * as every other "?" in ora and the Outlook pane (story 28633).
 *
 * The tooltip carries the helper copy that used to sit under the field, so
 * the form stays compact and the wording is still reachable. Unlike the native
 * `title` this replaced, the anchor takes keyboard focus (focus opens the
 * tooltip) and opens on tap on touch devices (CustomTooltip's MobileContext
 * handling).
 *
 * It sits INSIDE the field's <label> at every call site, so it is a
 * `role="button"` span rather than a <button> (a <button> is a labelable
 * element, which a <label> may not contain), and its name is the short
 * "More information" rather than the help text: a label's descendants feed
 * the field's own accessible name. `describeChild` makes the help text the
 * anchor's description while the tooltip is open; the title is passed as a
 * node because a STRING title under `describeChild` makes MUI put a native
 * `title` back on the anchor while closed — the browser bubble this replaces.
 */
import { useId, type ReactNode } from "react";
import CustomTooltip from "~/common/components/CustomTooltip";
import QTooltipAnchorIcon from "~/common/components/svgs/QTooltipAnchorIcon";

export interface FieldHelpTooltipProps {
  /** Plain help copy. */
  text?: string;
  /** Rich help (e.g. Comfort Level's table) — wins over `text`. */
  content?: ReactNode;
  testId?: string;
}

/** The anchor's accessible name; the help text is its description. */
export const FIELD_HELP_ANCHOR_LABEL = "More information";

export default function FieldHelpTooltip({
  text,
  content,
  testId,
}: FieldHelpTooltipProps) {
  const id = `field-help-${useId().replace(/:/g, "")}`;
  return (
    <CustomTooltip
      id={id}
      title={content ?? <span>{text}</span>}
      placement="top"
      arrow
      describeChild
      // The launch form renders inside the pane's / ora's MUI Drawer
      // (z-index 1200): a popper at CustomTooltip's default 1000 would sit
      // behind it.
      isOnModal
    >
      <span
        role="button"
        tabIndex={0}
        aria-label={FIELD_HELP_ANCHOR_LABEL}
        className="inline-flex shrink-0 items-center"
        data-testid={testId}
      >
        <QTooltipAnchorIcon id={id} />
      </span>
    </CustomTooltip>
  );
}
